import { useAuth } from "@/context/AuthContext";
import { BookmarkPlus, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Follow toggle for a user, shown at the end of the author cluster
 * (Thank · [Tip] · Follow). Uses the app's universal bookmark follow icon.
 * Hidden for your own posts (you can't follow yourself).
 */
export default function FollowUserButton({ userId, name = "", size = "sm", example = false }) {
    const { user, isFollowing, toggleFollow } = useAuth();
    if (!userId) return null;
    if (user && user.id === userId) return null;

    const following = isFollowing("user", userId);
    const dim = size === "md" ? "h-8 w-8" : "h-6 w-6";
    const icon = size === "md" ? "h-4 w-4" : "h-3.5 w-3.5";
    const handle = name ? `${String(name).replace(/^@/, "")}` : "this member";

    const onClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (example) { toast.info("This is an example profile and can't be followed or contacted."); return; }
        if (!user) { toast.error("Log in to follow"); return; }
        const res = await toggleFollow("user", userId);
        if (!res.ok) { toast.error(res.error || "Could not update follow"); return; }
        toast.success(res.following ? `Following ${handle}` : `Unfollowed ${handle}`);
    };

    return (
        <button
            type="button"
            data-testid="follow-user-button"
            onClick={onClick}
            title={example ? "Example profile — can't be followed or contacted" : (following ? `Following ${handle} — click to unfollow` : `Follow ${handle}`)}
            aria-label={following ? "Following" : "Follow"}
            className={`${dim} inline-flex items-center justify-center rounded-full border transition-colors ${following ? "bg-[#a855f7]/20 border-[#a855f7] text-[#a855f7]" : "bg-[#141414] border-[#2a2a2a] text-[#a0a0a0] hover:border-[#a855f7] hover:text-[#a855f7]"}`}
        >
            {following ? <BookmarkCheck className={icon} /> : <BookmarkPlus className={icon} />}
        </button>
    );
}
